import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const ThemeContext = createContext(null);

const STORAGE_KEY = 'bib-theme';
const MODES = ['light', 'dark', 'system'];

const readStored = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return MODES.includes(saved) ? saved : 'system';
  } catch {
    // Private browsing, or site data blocked. Follow the OS instead.
    return 'system';
  }
};

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(readStored);
  const [systemIsDark, setSystemIsDark] = useState(
    () => window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  // Keep following the OS while the user is on "system".
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e) => setSystemIsDark(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (mode === 'system') {
      root.removeAttribute('data-theme');
    } else {
      root.setAttribute('data-theme', mode);
    }

    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // Nothing to do — the theme still applies for this visit.
    }
  }, [mode]);

  const isDark = mode === 'dark' || (mode === 'system' && systemIsDark);

  // Toggling from "system" commits to the opposite of what you're looking at.
  const toggle = useCallback(() => setMode(isDark ? 'light' : 'dark'), [isDark]);

  const value = useMemo(
    () => ({ mode, setMode, isDark, toggle }),
    [mode, isDark, toggle]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
